import React, { useEffect, useRef, useMemo, memo } from "react";
import dayjs from "dayjs";
import GoogleMapReact from 'google-map-react';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { defaultMapProps, mapApiKey, getStillageTypeData } from "services/stillageDashboard";
import PropTypes from 'prop-types';
import MapWrapper from "../MapWrapper";

const StillageMap = ({ stillages }) => {
    const stils = useMemo(() => getStillageTypeData(stillages), [stillages]);
    // Store map and maps objects
    const mapRef = useRef();
    const mapsRef = useRef();
    const markersRef = useRef([]);
    const markerClustererRef = useRef(null);

    // When Google Maps API is loaded, store map and maps
    const handleApiLoaded = ({ map, maps }) => {
        mapRef.current = map;
        mapsRef.current = maps;
    };

    // Update markers/clusters when stillages changes
    useEffect(() => {
        const map = mapRef.current;
        const maps = mapsRef.current;
        if (!map || !maps || !Array.isArray(stils)) return;

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        markerClustererRef.current?.clearMarkers();

        // Create new markers
        const markers = stils.map(stillage => {
            const marker = new maps.Marker({
                position: { lat: stillage.lat, lng: stillage.long },
                icon: {
                    url: "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 384 512'><path fill='%230d6efd' d='M192 0C86 0 0 86 0 192c0 77.4 27.6 99.7 172.7 310.3a24 24 0 0 0 38.6 0C356.4 291.7 384 269.4 384 192 384 86 298 0 192 0zm0 272a80 80 0 1 1 0-160 80 80 0 0 1 0 160z'/></svg>",
                    scaledSize: { width: 40, height: 40 }
                }
            });

            // InfoWindow content
            const infoContent = `
                <table class="table table-borderless table-sm table-condensed" style="margin-bottom:0;">
                    <tbody>
                    <tr><th>Stillage No</th><td>${stillage.stillageNo}</td></tr>
                    <tr><th>Stillage Type</th><td>${stillage.stillageType.label}</td></tr>
                    <tr><th>Battery</th><td>${stillage.battery ?? ""}</td></tr>
                    <tr><th>Device Name</th><td>${stillage.trackerName ?? ""}</td></tr>
                    <tr><th>Cust Name</th><td>${stillage.custName ?? 'N/A'}</td></tr>
                    <tr><th>Last Poll</th><td>${stillage.lastPoll ? dayjs(stillage.lastPoll).format('DD/MM/YYYY') : ""}</td></tr>
                    </tbody>
                </table>`;
            const infoWindow = new maps.InfoWindow({
                content: infoContent
            });

            marker.addListener("mouseover", () => {
                infoWindow.open(map, marker);
            });
            marker.addListener("mouseout", () => {
                infoWindow.close();
            });

            return marker;
        });

        markerClustererRef.current = new MarkerClusterer({ map, markers });

        // Save markers for cleanup
        markersRef.current = markers;
    }, [stils]);

    return (
        // Important! Always set the container height explicitly
        <MapWrapper height={'400px'} width={'100%'}>
            <GoogleMapReact
                bootstrapURLKeys={{ key: mapApiKey }}
                defaultCenter={defaultMapProps.center}
                defaultZoom={defaultMapProps.zoom}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={handleApiLoaded}
            >
            </GoogleMapReact>
        </MapWrapper>
    );
}

StillageMap.propTypes = {
    stillages: PropTypes.array
};

export default memo(StillageMap);